import { useAtom } from "jotai";
import { useRef } from "react";
import { useBlocker } from "react-router-dom";
import { hasUnsavedFinanceDraft } from "web_myplann/Finances/Helpers/hasUnsavedFinanceDraft";
import { isFinancesWizardPath } from "web_myplann/Finances/Helpers/isFinancesWizardPath";
import { useFinanceEntries } from "web_myplann/Finances/Hooks/useFinanceEntries";
import { financeState } from "web_myplann/Finances/State/FinanceAtom";

export const useLeaveFinancesGuard = (hasUnconfirmedChanges = false) => {
	const [draft, setDraft] = useAtom(financeState);
	const { incomeEntries, expenseEntries, hasIncomeData, hasExpenseData } =
		useFinanceEntries();
	const isLeaveAllowed = useRef(false);

	const saved = { income: incomeEntries, expenses: expenseEntries };
	const hasUnsavedDraft = hasUnsavedFinanceDraft({
		draft,
		saved,
		isSavedLoaded: hasIncomeData && hasExpenseData,
	});
	const hasUnsavedChanges = hasUnconfirmedChanges || hasUnsavedDraft;

	const blocker = useBlocker(
		({ nextLocation }) =>
			hasUnsavedChanges &&
			!isLeaveAllowed.current &&
			!isFinancesWizardPath(nextLocation.pathname),
	);

	const stay = () => blocker.reset?.();
	const leave = () => {
		setDraft(saved);
		blocker.proceed?.();
	};

	const runUnguarded = (action: () => void) => {
		isLeaveAllowed.current = true;
		try {
			action();
		} finally {
			isLeaveAllowed.current = false;
		}
	};

	return {
		isLeaving: blocker.state === "blocked",
		stay,
		leave,
		runUnguarded,
	};
};
